import { Fragment, type ReactNode } from 'react';
import '../theme/themePreferences.css';
import { LegalPolicyLinks } from './LegalPolicyLinks';
import { readBuildMetadata, type VersionComponentMetadata } from './types';

export interface SiteAboutPanelProps {
  productName?: string;
  privacyPath?: string;
  cookiesPath?: string;
  termsPath?: string;
  supportEmail?: string | null;
  legalEmail?: string | null;
  /** Optional content rendered after Platform Build and before Legal & policies. */
  coreComponents?: ReactNode;
}

const COMPONENT_META_FIELDS: { key: keyof VersionComponentMetadata; label: string; mono?: boolean }[] =
  [
    { key: 'name', label: 'Name' },
    { key: 'description', label: 'Description' },
    { key: 'buildName', label: 'Build name' },
    { key: 'buildVersion', label: 'Build version', mono: true }
  ];

interface MetaRow {
  label: string;
  value: string;
  mono?: boolean;
}

function displayValue(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '—';
}

function MetaGrid({ rows }: { rows: MetaRow[] }) {
  if (rows.length === 0) {
    return <p className="theme-preferences__hint">Build information is not available.</p>;
  }

  return (
    <dl className="theme-preferences__meta-grid theme-preferences__meta-grid--wide">
      {rows.map(({ label, value, mono }) => (
        <Fragment key={label}>
          <dt>{label}</dt>
          <dd className={mono ? 'theme-preferences__meta-value--mono' : undefined}>{value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

export function SiteAboutPanel({
  productName,
  privacyPath = '/settings/privacy',
  cookiesPath = '/settings/cookies',
  termsPath = '/settings/terms',
  supportEmail,
  legalEmail,
  coreComponents
}: SiteAboutPanelProps) {
  const meta = readBuildMetadata();
  const site = meta.site ?? null;
  const web = (meta.web ?? meta.ui) as VersionComponentMetadata | null | undefined;
  const siteName =
    productName?.trim() || site?.name?.trim() || web?.name?.trim() || meta.name?.trim() || 'This application';
  const siteDescription = site?.description?.trim();
  const builtAt = meta.buildTime ? new Date(meta.buildTime).toLocaleString() : null;
  const buildRows: MetaRow[] = [
    ...(site
      ? COMPONENT_META_FIELDS.map(({ key, label, mono }) => ({
          label,
          mono,
          value: displayValue(site[key] as string | null | undefined)
        }))
      : []),
    { label: 'Built', value: builtAt ?? '—' },
    { label: 'Git commit', value: displayValue(meta.gitCommit), mono: true }
  ];

  return (
    <div className="user-settings-sections">
      <section className="theme-preferences theme-preferences--first" aria-labelledby="site-about-heading">
        <h3 id="site-about-heading" className="theme-preferences__title">
          About
        </h3>
        <p className="theme-preferences__hint">
          {siteDescription ?? `${siteName} — installed build information for the site you are signed into.`}
        </p>
      </section>

      <section className="theme-preferences" aria-labelledby="site-about-build-heading">
        <h3 id="site-about-build-heading" className="theme-preferences__title">
          Platform Build
        </h3>
        <p className="theme-preferences__hint">
          The installed build of this site, when it was built, and the source revision it came from.
        </p>
        <MetaGrid rows={buildRows} />
      </section>

      {coreComponents ? (
        <section className="theme-preferences" aria-labelledby="site-about-core-components-heading">
          <h3 id="site-about-core-components-heading" className="theme-preferences__title">
            Core Components
          </h3>
          {coreComponents}
        </section>
      ) : null}

      <section className="theme-preferences" aria-labelledby="site-about-legal-heading">
        <h3 id="site-about-legal-heading" className="theme-preferences__title">
          Legal &amp; policies
        </h3>
        <p className="theme-preferences__hint">
          Privacy, cookies, terms of use, and contact information for this site.
        </p>
        <LegalPolicyLinks
          privacyPath={privacyPath}
          cookiesPath={cookiesPath}
          termsPath={termsPath}
          supportEmail={supportEmail}
          legalEmail={legalEmail}
        />
      </section>
    </div>
  );
}
